import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Textarea, FormControl,
  FormLabel, Box, Text, Badge, useToast, Flex, IconButton, Checkbox,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb, useDisclosure,
  Select, Tag, TagLabel, TagCloseButton, Wrap, Divider, Tooltip,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2, FiMenu, FiUser, FiUsers, FiClipboard, FiKey, FiUploadCloud } from 'react-icons/fi';
import api from '../api';
import useMenuPosition from '../hooks/useMenuPosition';
import AssignUserModal from './AssignUserModal';
import AddContactModal from './AddContactModal';
import TaskProcSafeModal from './TaskProcSafeModal';
import { useAuth } from '../contexts/AuthContext';

export default function ProgressModal({ taskId, taskTitle, isOpen, onClose, onRefresh }) {
  const { user } = useAuth();
  const [subTasks, setSubTasks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', percentage_contribution: 10, projected_hours: '' });
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [stContextMenu, setStContextMenu] = useState(null);
  const stContextMenuRef = useRef(null);
  const stContextMenuPos = useMenuPosition(stContextMenuRef, stContextMenu);
  const [assignTarget, setAssignTarget] = useState(null);
  const assignDisclosure = useDisclosure();
  const [contactTarget, setContactTarget] = useState(null);
  const contactDisclosure = useDisclosure();
  // Resource state
  const [allResources, setAllResources] = useState([]);
  const [subTaskResources, setSubTaskResources] = useState({}); // { subTaskId: [resources] }
  const [expandedResources, setExpandedResources] = useState({}); // { subTaskId: bool }
  // ── ToDo-list assignment state ────────────────────────────────────────────
  // The add/edit form lets the user push the sub-task onto ToDo lists:
  // their own via "+ Me", or colleagues' via the type-ahead search (gated
  // behind the same see_others_todo setting as the ToDo page user picker).
  const [allUsers, setAllUsers] = useState([]);          // /calendar/users
  const [canPickOthers, setCanPickOthers] = useState(false);
  const [myGroups, setMyGroups] = useState([]);          // caller's todo groups (category names)
  const [todoUsers, setTodoUsers] = useState([]);        // selected [{id, name}]
  const [todoGroupName, setTodoGroupName] = useState('');// chosen category name ('' = none)
  const [userSearch, setUserSearch] = useState('');
  const [linkedUsers, setLinkedUsers] = useState([]);    // users who already have this sub-task (edit mode)
  const [procSafeTarget, setProcSafeTarget] = useState(null); // { subTaskId, tab, name } — Procedures & Passwords panel
  const toast = useToast();

  const fetchSubTasks = useCallback(async () => {
    if (!taskId) return;
    try { const { data } = await api.get(`/sub-tasks?task_id=${taskId}`); setSubTasks(data); } catch(e) {}
  }, [taskId]);

  const fetchResources = useCallback(async () => {
    try { const { data } = await api.get('/resources'); setAllResources(data || []); } catch(e) {}
  }, []);

  const fetchSubTaskResources = useCallback(async (stId) => {
    try {
      const { data } = await api.get(`/sub-tasks/${stId}/resources`);
      setSubTaskResources(prev => ({ ...prev, [stId]: data || [] }));
    } catch(e) {}
  }, []);

  useEffect(() => {
    if (isOpen && taskId) {
      fetchSubTasks();
      fetchResources();
      setShowForm(false);
      setEditingId(null);
      setTodoUsers([]); setUserSearch(''); setLinkedUsers([]);
    }
  }, [isOpen, taskId, fetchSubTasks, fetchResources]);

  // Load ToDo-assignment support data once per open: the user list, the
  // caller's todo groups (for the category picker — group names are matched
  // per-user server-side), and the see_others_todo gate for cross-user
  // assignment. Failures degrade gracefully: no users → only "+ Me" works;
  // no groups → items land ungrouped.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    api.get('/calendar/users')
      .then(r => { if (!cancelled) setAllUsers(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    api.get('/todo-groups')
      .then(r => {
        if (cancelled) return;
        const gs = Array.isArray(r.data) ? r.data : [];
        setMyGroups(gs);
        // Default the category to "Today" when the user has that group —
        // it's the system-seeded bucket and the most common destination.
        const today = gs.find(g => String(g.name).toLowerCase() === 'today');
        setTodoGroupName(today ? today.name : '');
      })
      .catch(() => {});
    const isAdmin = user?.role === 'admin';
    api.get('/settings/see_others_todo')
      .then(r => {
        if (cancelled) return;
        const v = r.data?.setting_value;
        const enabled = v == null ? true : String(v).toLowerCase() !== 'false';
        setCanPickOthers(enabled || isAdmin);
      })
      .catch(() => { if (!cancelled) setCanPickOthers(true); });
    return () => { cancelled = true; };
  }, [isOpen, user]);

  // Who already has this sub-task on their ToDo list (shown in edit mode).
  const fetchLinkedUsers = useCallback(async (stId) => {
    try {
      const { data } = await api.get(`/todo-items/linked-users?subtask_id=${stId}`);
      setLinkedUsers(Array.isArray(data) ? data : []);
    } catch (e) { setLinkedUsers([]); }
  }, []);

  const completedPct = subTasks.reduce((s, st) => s + (st.is_completed ? (parseFloat(st.percentage_contribution) || 0) : 0), 0);
  const totalPct = subTasks.reduce((s, st) => s + (parseFloat(st.percentage_contribution) || 0), 0);
  const totalProjected = subTasks.reduce((s, st) => s + (parseFloat(st.projected_hours) || 0), 0);
  const totalActual = subTasks.reduce((s, st) => s + (parseFloat(st.actual_hours) || 0), 0);

  const resetForm = () => {
    setForm({ title: '', description: '', percentage_contribution: 10, projected_hours: '' });
    setTodoUsers([]); setUserSearch(''); setLinkedUsers([]);
  };

  // Push the saved sub-task onto the selected users' ToDo lists. Runs after
  // the sub-task itself has saved; failures here never roll back the
  // sub-task — they surface as a toast so the user can retry from Edit.
  const assignToTodoLists = async (subtaskId) => {
    if (!subtaskId || todoUsers.length === 0) return;
    try {
      const { data } = await api.post('/todo-items/assign', {
        subtask_id: subtaskId,
        user_ids: todoUsers.map(u => u.id),
        group_name: todoGroupName || null,
      });
      const results = Array.isArray(data?.results) ? data.results : [];
      const added = results.filter(r => r.status === 'added');
      const exists = results.filter(r => r.status === 'exists');
      const failed = results.filter(r => r.status === 'forbidden' || r.status === 'not_found');
      const bits = [];
      if (added.length) bits.push(`Added to ${added.map(r => r.name || 'user').join(', ')}`);
      if (exists.length) bits.push(`${exists.map(r => r.name || 'user').join(', ')} already had it`);
      if (failed.length) bits.push(`${failed.length} could not be added`);
      if (bits.length) {
        toast({ title: 'ToDo lists', description: bits.join(' · '),
          status: failed.length ? 'warning' : 'success', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Sub-task saved, but adding to ToDo lists failed', status: 'warning', duration: 3000 });
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast({ title: 'Title required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      let subtaskId = editingId;
      if (editingId) {
        await api.put(`/sub-tasks/${editingId}`, form);
      } else {
        const { data } = await api.post('/sub-tasks', { ...form, task_id: taskId, sort_order: subTasks.length });
        subtaskId = data?.id || null;
      }
      await assignToTodoLists(subtaskId);
      await fetchSubTasks(); setShowForm(false); setEditingId(null); resetForm();
      toast({ title: editingId ? 'Updated' : 'Sub-task added', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleToggle = async (id) => {
    try {
      const { data } = await api.put(`/sub-tasks/${id}/toggle`);
      await fetchSubTasks();
      if (data.taskCompletion >= 100) toast({ title: 'Task 100% complete!', status: 'success', duration: 2000 });
      if (onRefresh) onRefresh();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const openEdit = (st) => {
    setForm({ title: st.title, description: st.description || '', percentage_contribution: st.percentage_contribution || 0, projected_hours: st.projected_hours || '' });
    setTodoUsers([]); setUserSearch('');
    setEditingId(st.id); setShowForm(true);
    fetchLinkedUsers(st.id);
  };

  // Resource management
  const toggleResourcePanel = (stId) => {
    const opening = !expandedResources[stId];
    setExpandedResources(prev => ({ ...prev, [stId]: opening }));
    if (opening && !subTaskResources[stId]) fetchSubTaskResources(stId);
  };

  const addResource = async (stId, resourceId) => {
    if (!resourceId) return;
    try {
      await api.post(`/sub-tasks/${stId}/resources`, { resource_id: resourceId });
      fetchSubTaskResources(stId);
      toast({ title: 'Resource added provisionally', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const removeResource = async (stId, resourceId) => {
    try {
      await api.delete(`/sub-tasks/${stId}/resources/${resourceId}`);
      fetchSubTaskResources(stId);
    } catch(e) {}
  };

  const handleDragStart = (idx) => (e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (idx) => (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverIdx(idx); };
  const handleDrop = (idx) => async (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    const reordered = [...subTasks];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setSubTasks(reordered);
    setDragIdx(null); setDragOverIdx(null);
    try { await api.put('/sub-tasks/reorder', { items: reordered.map((st, i) => ({ id: st.id, sort_order: i })) }); }
    catch(e) { fetchSubTasks(); }
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const pctColor = completedPct >= 100 ? 'green' : completedPct >= 50 ? 'blue' : completedPct > 0 ? 'orange' : 'gray';

  const typeEmoji = (r) => r?.is_person ? '👤' : r?.type_name === 'Room' ? '🏠' : '📦';

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>
            <Box>
              <Text>Sub-Tasks / To-Do</Text>
              <Text fontSize="11px" color="gray.500" fontWeight="400">{taskTitle || 'Task'}</Text>
            </Box>
            <HStack spacing={2}>
              <Badge colorScheme={pctColor} fontSize="13px" px={2}>{Math.round(completedPct)}%</Badge>
              {totalProjected > 0 && <Badge colorScheme="purple" fontSize="10px">{totalActual.toFixed(1)}h / {totalProjected.toFixed(1)}h</Badge>}
              <Button size="xs" leftIcon={<FiPlus />} colorScheme="teal" variant="outline"
                onClick={() => { setShowForm(!showForm); setEditingId(null); resetForm(); }}>
                {showForm && !editingId ? 'Cancel' : 'Add'}
              </Button>
            </HStack>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {/* Progress bar */}
          <Box mb={3}>
            <Box bg="gray.100" h="12px" borderRadius="full" overflow="hidden">
              <Box h="100%" w={`${Math.min(completedPct, 100)}%`} bg={`${pctColor}.400`}
                transition="width 0.3s" borderRadius="full" />
            </Box>
            {totalPct > 0 && totalPct < 100 && (
              <Text fontSize="8px" color="orange.500" textAlign="right" mt={0.5}>
                Sub-tasks total {totalPct.toFixed(0)}% (should be 100%)
              </Text>
            )}
          </Box>

          {/* Add/Edit form */}
          {showForm && (
            <Box bg="teal.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Sub-Task Title *</FormLabel>
                  <Input size="sm" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. Script review, Camera setup" autoFocus /></FormControl>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                  <Textarea size="sm" rows={2} value={form.description}
                    onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></FormControl>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={2}>
                    <FormLabel fontSize="xs" mb={0.5}>% Contribution ({form.percentage_contribution}%)</FormLabel>
                    <Slider min={0} max={100} step={1} value={form.percentage_contribution}
                      onChange={v => setForm(p => ({ ...p, percentage_contribution: v }))}>
                      <SliderTrack><SliderFilledTrack bg="teal.400" /></SliderTrack>
                      <SliderThumb boxSize={4} />
                    </Slider>
                  </FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Projected Hours</FormLabel>
                    <Input size="sm" type="number" step="0.25" value={form.projected_hours}
                      onChange={e => setForm(p => ({ ...p, projected_hours: e.target.value }))} placeholder="Optional" /></FormControl>
                </HStack>

                {/* ── Add to ToDo List ─────────────────────────────────────
                    Push this sub-task onto ToDo lists on save. "+ Me" adds
                    the caller; the search adds colleagues (visible only when
                    the see_others_todo feature allows it). The category
                    select maps to each target user's group of the same name
                    ("Today"/"This Week" are seeded for everyone). */}
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Add to ToDo List</FormLabel>
                  {linkedUsers.length > 0 && (
                    <Text fontSize="9px" color="gray.500" mb={1}>
                      Already on: {linkedUsers.map(u => u.display_name || u.username).join(', ')}
                    </Text>
                  )}
                  {todoUsers.length > 0 && (
                    <Wrap spacing={1} mb={1.5}>
                      {todoUsers.map(u => (
                        <Tag key={u.id} size="sm" colorScheme="teal" variant="subtle">
                          <TagLabel>{u.name}</TagLabel>
                          <TagCloseButton onClick={() =>
                            setTodoUsers(prev => prev.filter(x => x.id !== u.id))} />
                        </Tag>
                      ))}
                    </Wrap>
                  )}
                  <HStack spacing={2} align="start">
                    {user?.id && !todoUsers.find(u => u.id === user.id)
                      && !linkedUsers.find(u => u.id === user.id) && (
                      <Button size="xs" variant="outline" colorScheme="teal" leftIcon={<FiUser />}
                        onClick={() => setTodoUsers(prev =>
                          [...prev, { id: user.id, name: user.display_name || user.username || 'Me' }])}>
                        My List
                      </Button>
                    )}
                    {canPickOthers && (
                      <Box flex={1} position="relative">
                        <Input size="xs" placeholder="Search users to add…" value={userSearch}
                          onChange={e => setUserSearch(e.target.value)} />
                        {userSearch.trim().length >= 1 && (
                          <Box position="absolute" top="100%" left={0} right={0} zIndex={20}
                            bg="white" border="1px" borderColor="gray.200" borderRadius="md"
                            boxShadow="md" maxH="150px" overflowY="auto" mt={0.5}>
                            {allUsers
                              .filter(u => u.id !== user?.id)
                              .filter(u => !todoUsers.find(x => x.id === u.id))
                              .filter(u => !linkedUsers.find(x => x.id === u.id))
                              .filter(u => {
                                const q = userSearch.trim().toLowerCase();
                                return (u.display_name || '').toLowerCase().includes(q)
                                  || (u.username || '').toLowerCase().includes(q)
                                  || (u.email || '').toLowerCase().includes(q);
                              })
                              .slice(0, 8)
                              .map(u => (
                                <Box key={u.id} px={2} py={1} fontSize="11px" cursor="pointer"
                                  _hover={{ bg: 'teal.50' }}
                                  onClick={() => {
                                    setTodoUsers(prev => [...prev,
                                      { id: u.id, name: u.display_name || u.username }]);
                                    setUserSearch('');
                                  }}>
                                  {u.display_name || u.username}
                                  {u.email ? <Text as="span" color="gray.400"> · {u.email}</Text> : null}
                                </Box>
                              ))}
                            {allUsers.filter(u => u.id !== user?.id).filter(u => {
                              const q = userSearch.trim().toLowerCase();
                              return (u.display_name || '').toLowerCase().includes(q)
                                || (u.username || '').toLowerCase().includes(q)
                                || (u.email || '').toLowerCase().includes(q);
                            }).filter(u => !todoUsers.find(x => x.id === u.id))
                              .filter(u => !linkedUsers.find(x => x.id === u.id)).length === 0 && (
                              <Box px={2} py={1} fontSize="10px" color="gray.400">No matches</Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                    {todoUsers.length > 0 && (
                      <Select size="xs" w="130px" value={todoGroupName}
                        onChange={e => setTodoGroupName(e.target.value)}>
                        <option value="">No category</option>
                        {myGroups.map(g => (
                          <option key={g.id} value={g.name}>{g.name}</option>
                        ))}
                      </Select>
                    )}
                  </HStack>
                  {todoUsers.length > 0 && (
                    <Text fontSize="8px" color="gray.400" mt={0.5}>
                      Added to the selected {todoUsers.length === 1 ? "user's" : "users'"} ToDo
                      list{todoUsers.length === 1 ? '' : 's'} when you save. The category is matched
                      by name on each user's own list.
                    </Text>
                  )}
                </FormControl>

                <Button size="xs" colorScheme="teal" onClick={handleSave} isLoading={saving} alignSelf="flex-end">
                  {editingId ? 'Update' : 'Add Sub-Task'}
                </Button>
              </VStack>
            </Box>
          )}

          {/* Sub-task list */}
          {subTasks.length === 0 && !showForm ? (
            <VStack py={8} spacing={2}>
              <Text fontSize="sm" color="gray.500">No sub-tasks yet.</Text>
              <Text fontSize="11px" color="gray.400">Add sub-tasks to break this work into trackable items.</Text>
            </VStack>
          ) : (
            <VStack spacing={1} align="stretch">
              {subTasks.map((st, idx) => (
                <Box key={st.id} p={2} border="1px" borderRadius="md"
                  borderColor={dragOverIdx === idx ? 'teal.400' : st.is_completed ? 'green.200' : 'gray.100'}
                  bg={dragOverIdx === idx ? 'teal.50' : st.is_completed ? 'green.50' : 'white'}
                  opacity={dragIdx === idx ? 0.4 : st.is_completed ? 0.8 : 1}
                  draggable onDragStart={handleDragStart(idx)} onDragOver={handleDragOver(idx)}
                  onDrop={handleDrop(idx)} onDragEnd={handleDragEnd}
                  onContextMenu={e => { e.preventDefault(); setStContextMenu({ x: e.clientX, y: e.clientY, st }); }}
                  transition="border-color 0.15s, background 0.15s">
                  <Flex align="start" gap={2}>
                    <Box cursor="grab" color="gray.400" _hover={{ color: 'gray.600' }} mt={1}
                      onMouseDown={e => e.stopPropagation()} draggable="false">
                      <FiMenu size={14} />
                    </Box>
                    <Checkbox isChecked={!!st.is_completed} onChange={() => handleToggle(st.id)}
                      colorScheme="green" mt={0.5} size="lg" />
                    <Box flex={1}>
                      <Flex justify="space-between" align="center">
                        <Text fontSize="12px" fontWeight="600"
                          textDecoration={st.is_completed ? 'line-through' : 'none'}
                          color={st.is_completed ? 'gray.500' : 'gray.800'}>
                          {st.title}
                        </Text>
                        <HStack spacing={1}>
                          {st.assigned_to_name && (
                            <Badge colorScheme="purple" fontSize="8px" variant="subtle"
                              cursor="pointer"
                              onClick={() => { setAssignTarget(st); assignDisclosure.onOpen(); }}>
                              👤 {st.assigned_to_name}
                            </Badge>
                          )}
                          <Badge colorScheme="teal" fontSize="9px">{parseFloat(st.percentage_contribution || 0).toFixed(0)}%</Badge>
                          {st.projected_hours && (
                            <Badge colorScheme="purple" fontSize="8px">{parseFloat(st.actual_hours || 0).toFixed(1)}h / {parseFloat(st.projected_hours).toFixed(1)}h</Badge>
                          )}
                          {/* Resources badge */}
                          {subTaskResources[st.id]?.length > 0 && (
                            <Badge colorScheme="orange" fontSize="8px" cursor="pointer"
                              onClick={() => toggleResourcePanel(st.id)}>
                              📦 {subTaskResources[st.id].length}
                            </Badge>
                          )}
                          {/* Procedures & Password Safe (migration 161) —
                              dimmed when nothing attached, badge with count
                              when there is. Password count is per-user. */}
                          <Box position="relative" display="inline-block">
                            <Tooltip label="Procedures"><IconButton icon={<FiClipboard size={12} />} size="xs" variant="ghost"
                              color={st.procedure_count > 0 ? 'teal.600' : 'gray.300'}
                              _hover={{ color: 'teal.500' }}
                              onClick={() => setProcSafeTarget({ subTaskId: st.id, tab: 'procedures', name: st.title })} aria-label="Procedures" /></Tooltip>
                            {st.procedure_count > 0 && <Text fontSize="7px" fontWeight="700" color="teal.600" position="absolute" top="-3px" right="-4px" pointerEvents="none">{st.procedure_count}</Text>}
                          </Box>
                          <Box position="relative" display="inline-block">
                            <Tooltip label="Password Safe"><IconButton icon={<FiKey size={12} />} size="xs" variant="ghost"
                              color={st.password_count > 0 ? 'purple.600' : 'gray.300'}
                              _hover={{ color: 'purple.500' }}
                              onClick={() => setProcSafeTarget({ subTaskId: st.id, tab: 'passwords', name: st.title })} aria-label="Password Safe" /></Tooltip>
                            <Tooltip label="Client Upload Portals"><IconButton icon={<FiUploadCloud size={12} />} size="xs" variant="ghost"
                              colorScheme="blue" aria-label="uploads"
                              onClick={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('forgebos:open-upload-portals', { detail: { subTaskId: st.id, name: st.title || 'Sub-task' } })); }} /></Tooltip>
                            {st.password_count > 0 && <Text fontSize="7px" fontWeight="700" color="purple.600" position="absolute" top="-3px" right="-4px" pointerEvents="none">{st.password_count}</Text>}
                          </Box>
                          <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => openEdit(st)} aria-label="Edit" />
                          <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                            onClick={async () => { await api.delete(`/sub-tasks/${st.id}`); fetchSubTasks(); if (onRefresh) onRefresh(); }} aria-label="Delete" />
                        </HStack>
                      </Flex>
                      {/* Description: skip the literal string "0" too — old
                          rows sometimes had "0" as a placeholder description. */}
                      {(st.description && String(st.description).trim() && String(st.description).trim() !== '0') ? (
                        <Text fontSize="10px" color="gray.500" mt={0.5}>{st.description}</Text>
                      ) : null}
                      {/* `is_completed && completed_at && (...)` rendered the
                          literal "0" under every incomplete sub-task: MySQL
                          returns TINYINT(1) as the number 0, and `0 && X`
                          short-circuits to 0, which React stringifies inline.
                          Force boolean before the guard so we render nothing. */}
                      {!!st.is_completed && st.completed_at && (
                        <Text fontSize="9px" color="green.500" mt={0.5}>
                          Completed {new Date(st.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </Text>
                      )}

                      {/* Provisional Resources Panel */}
                      <Box mt={1}>
                        <Text fontSize="9px" color="orange.500" cursor="pointer" fontWeight="600"
                          onClick={() => toggleResourcePanel(st.id)}
                          _hover={{ color: 'orange.700' }}>
                          {expandedResources[st.id] ? '▾' : '▸'} Provisional Resources
                          {subTaskResources[st.id]?.length > 0 ? ` (${subTaskResources[st.id].length})` : ''}
                        </Text>
                        {expandedResources[st.id] && (
                          <Box mt={1} pl={2} borderLeft="2px solid" borderColor="orange.200">
                            <Text fontSize="9px" color="gray.400" mb={1}>
                              Resources listed here are not booked — they are provisional allocations only.
                            </Text>
                            {/* Existing resources */}
                            {(subTaskResources[st.id] || []).length > 0 && (
                              <Wrap spacing={1} mb={2}>
                                {(subTaskResources[st.id] || []).map(r => (
                                  <Tag key={r.resource_id} size="sm" colorScheme="orange" variant="subtle">
                                    <TagLabel>{typeEmoji(r)} {r.resource_name}</TagLabel>
                                    <TagCloseButton onClick={() => removeResource(st.id, r.resource_id)} />
                                  </Tag>
                                ))}
                              </Wrap>
                            )}
                            {/* Add resource dropdown */}
                            <Select size="xs" placeholder="+ Add provisional resource..."
                              onChange={e => { if (e.target.value) { addResource(st.id, e.target.value); e.target.value = ''; } }}>
                              {allResources
                                .filter(r => !(subTaskResources[st.id] || []).find(x => String(x.resource_id) === String(r.id)))
                                .map(r => (
                                  <option key={r.id} value={r.id}>
                                    {r.is_person ? '👤' : r.type_name === 'Room' ? '🏠' : '📦'} {r.name}
                                  </option>
                                ))}
                            </Select>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Flex>
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Text fontSize="10px" color="gray.400" mr="auto">
            {subTasks.filter(s => s.is_completed).length}/{subTasks.length} completed
          </Text>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Sub-task Procedures & Password Safe panel */}
    <TaskProcSafeModal isOpen={!!procSafeTarget} subTaskId={procSafeTarget?.subTaskId}
      mode={procSafeTarget?.tab}
      title={procSafeTarget?.tab === 'passwords'
        ? `\u{1F510} ${procSafeTarget?.name || 'Sub-task'} \u2014 Password Safe`
        : `\u{1F4CB} ${procSafeTarget?.name || 'Sub-task'} \u2014 Procedures`}
      onClose={() => { setProcSafeTarget(null); fetchSubTasks(); }} />

    {/* Sub-task right-click context menu */}
    {stContextMenu && (
      <Box ref={stContextMenuRef}
        position="fixed" left={stContextMenuPos.left} top={stContextMenuPos.top}
        bg="white" border="1px" borderColor="gray.200" borderRadius="md" boxShadow="lg"
        zIndex={10000} py={1} minW="180px"
        maxH={stContextMenuPos.maxHeight} maxW={stContextMenuPos.maxWidth} overflowY="auto" overflowX="hidden"
        visibility={stContextMenuPos.ready ? 'visible' : 'hidden'}
        onClick={() => setStContextMenu(null)}
        onMouseLeave={() => setStContextMenu(null)}>
        <Box px={3} py={1} fontSize="10px" color="gray.400" fontWeight="600"
          borderBottom="1px" borderColor="gray.100" mb={0.5} noOfLines={1}>
          {stContextMenu.st.title}
        </Box>
        <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'teal.50' }} color="teal.600"
          display="flex" alignItems="center" gap={2}
          onClick={() => { setAssignTarget(stContextMenu.st); assignDisclosure.onOpen(); setStContextMenu(null); }}>
          <FiUser size={13} /> Assign User
        </Box>
        <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'purple.50' }} color="purple.600"
          display="flex" alignItems="center" gap={2}
          onClick={() => { setContactTarget(stContextMenu.st); contactDisclosure.onOpen(); setStContextMenu(null); }}>
          <FiUsers size={13} /> Add Contact
        </Box>
        <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'orange.50' }} color="orange.600"
          display="flex" alignItems="center" gap={2}
          onClick={() => { toggleResourcePanel(stContextMenu.st.id); setStContextMenu(null); }}>
          📦 Provisional Resources
        </Box>
        <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
          display="flex" alignItems="center" gap={2}
          onClick={() => { openEdit(stContextMenu.st); setStContextMenu(null); }}>
          <FiEdit2 size={13} /> Edit sub-task
        </Box>
      </Box>
    )}

    <AssignUserModal entityType="subtask" entityId={assignTarget?.id} entityName={assignTarget?.title}
      isOpen={assignDisclosure.isOpen} onClose={assignDisclosure.onClose} onSaved={fetchSubTasks} />
    <AddContactModal entityType="subtask" entityId={contactTarget?.id} entityName={contactTarget?.title}
      isOpen={contactDisclosure.isOpen} onClose={contactDisclosure.onClose} onSaved={fetchSubTasks} />
    </>
  );
}
